/**
 * Slug rules — the URL identifier for a creation's gallery/play page
 * (`/g/:slug`). Slugs are unique platform-wide, enforced by a claim doc at
 * `slugs/{slug}` (see {@link import('./repos/creations.js').CreationsRepo.create}).
 *
 * Rules: lowercase, ASCII alnum + single hyphens, no leading/trailing hyphen,
 * 3..48 chars. Matches the `GAME_ID_PATTERN` spirit from @djgg/core (a slug that
 * could later BE a gameId shouldn't need reshaping).
 */
import { PlatformError } from '@djgg/core';

/** The canonical slug pattern. */
export const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

export const SLUG_MIN_LEN = 3;
export const SLUG_MAX_LEN = 48;

/** True when `slug` is already in canonical form. */
export function isValidSlug(slug: string): boolean {
  return (
    typeof slug === 'string' &&
    slug.length >= SLUG_MIN_LEN &&
    slug.length <= SLUG_MAX_LEN &&
    SLUG_PATTERN.test(slug)
  );
}

/**
 * Best-effort slugify: lowercases, transliterates spaces/underscores to
 * hyphens, strips other non-alnum, collapses repeats, trims hyphens, and clamps
 * to {@link SLUG_MAX_LEN}. Returns '' when nothing usable remains (caller must
 * handle — an empty slug is never valid).
 */
export function slugify(input: string): string {
  const s = input
    .normalize('NFKD')
    // drop combining marks so accented letters reduce to their base
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LEN)
    // slicing can leave a trailing hyphen
    .replace(/-+$/g, '');
  return s;
}

/** Asset-name rules — the stable identifier a creator gives a generated asset
 *  ("tank", "arena-background") so build/iterate prompts can reference it
 *  unambiguously. Slug-shaped, shorter bounds: 2..40 chars. Unique among a
 *  creation's ATTACHED assets, case-insensitively (enforced inside
 *  {@link import('./repos/creations.js').CreationsRepo.attachAsset}'s CAS). */
export const ASSET_NAME_MIN_LEN = 2;
export const ASSET_NAME_MAX_LEN = 40;

/** True when `name` is a canonical asset name. */
export function isValidAssetName(name: string): boolean {
  return (
    typeof name === 'string' &&
    name.length >= ASSET_NAME_MIN_LEN &&
    name.length <= ASSET_NAME_MAX_LEN &&
    SLUG_PATTERN.test(name)
  );
}

/**
 * Random `[a-z0-9]{len}` suffix for fork-slug minting
 * (`parent-slug-x7k2qp`). 36^6 ≈ 2.2B — a collision is a retry, not a design
 * concern (CreationsRepo.createFork re-mints up to 3 times).
 */
export function randomSlugSuffix(len = 6): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < len; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return s;
}

/**
 * Slugify and assert validity. Throws `PlatformError('validation')` when the
 * result can't satisfy {@link isValidSlug} (too short / empty after cleaning).
 */
export function toValidSlug(input: string): string {
  const slug = slugify(input);
  if (!isValidSlug(slug)) {
    throw new PlatformError(
      'validation',
      `cannot derive a valid slug from ${JSON.stringify(input)} (need ${SLUG_MIN_LEN}-${SLUG_MAX_LEN} chars of [a-z0-9-])`,
    );
  }
  return slug;
}
